/**
 * Componenti UI di base condivisi — unica fonte per colori di stato e stili.
 */
import React from "react";

export const STATUS_STYLES = {
  ok: "bg-green-100 text-green-800",
  conforme: "bg-green-100 text-green-800",
  carenza: "bg-red-100 text-red-800",
  critico: "bg-red-100 text-red-800",
  sotto_normativa: "bg-red-100 text-red-800",
  attenzione: "bg-yellow-100 text-yellow-800",
  solo_copertura: "bg-blue-100 text-blue-800",
  solo_normativa: "bg-gray-100 text-gray-700",
  // Stati del motore di generazione automatica copertura (gap per mansione,
  // vedi domain/coperturaGenerator.js)
  sotto: "bg-red-100 text-red-800",
  sopra: "bg-blue-100 text-blue-800",
  non_configurato: "bg-yellow-100 text-yellow-800",
  turni_non_disponibili: "bg-yellow-100 text-yellow-800",
  peso_nullo: "bg-red-100 text-red-800",
  default: "bg-gray-100 text-gray-700",
};

export const STATUS_LABELS = {
  ok: "OK",
  carenza: "Carenza",
  sotto_normativa: "Sotto normativa",
  solo_copertura: "Solo copertura",
  solo_normativa: "Senza copertura",
  sotto: "Sotto richiesta",
  sopra: "Sopra richiesta",
  non_configurato: "Non configurato",
  turni_non_disponibili: "Turni non disponibili",
  peso_nullo: "Peso nullo",
};

export function Badge({ status, children }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
    >
      {children ?? STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function Card({ title, subtitle, actions, children, className = "" }) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}
    >
      {(title || actions) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            {title && <h3 className="font-semibold text-gray-800">{title}</h3>}
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function Spinner({ label = "Caricamento…" }) {
  return (
    <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
      <div className="animate-spin h-5 w-5 border-2 border-primary-600 border-t-transparent rounded-full" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function ErrorBox({ message, onRetry }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">
      <p>{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 text-red-700 underline hover:no-underline"
        >
          Riprova
        </button>
      )}
    </div>
  );
}

export function WarningList({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-3 text-xs space-y-1">
      {warnings.map((w, i) => (
        <p key={i}>⚠ {w.message || w.code}</p>
      ))}
    </div>
  );
}

export function StatCard({ label, value, unit, tone = "default" }) {
  const tones = {
    default: "text-gray-900",
    good: "text-green-700",
    bad: "text-red-700",
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-xl font-bold ${tones[tone]}`}>
        {value}
        {unit && (
          <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>
        )}
      </p>
    </div>
  );
}

/** Tabella minimale: columns = [{key, label, align?, render?}] */
export function SimpleTable({ columns, rows, rowKey }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
            {columns.map((c) => (
              <th
                key={c.key}
                className={`py-2 px-3 ${c.align === "right" ? "text-right" : ""}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey ? rowKey(row) : i}
              className="border-b border-gray-100 hover:bg-gray-50"
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`py-2 px-3 ${c.align === "right" ? "text-right tabular-nums" : ""}`}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-center text-gray-400"
              >
                Nessun dato
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
